import { ActionIcon } from '@mantine/core';
import { IconEdit, IconX } from '@tabler/icons-react';
import type { CSSProperties, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import classes from './WidgetCardChrome.module.css';

/**
 * WidgetCardChrome — the EDITOR's interactive card chrome (TV1-2291 · SDD §4, t12).
 *
 * The hover action bar and the three resize grips, rebuilt on design-system
 * tokens. What they replace: two `color="blue"` / `color="red"` `ActionIcon`s and
 * three inline-styled `#3498db` divs, none of which went through `light-dark()`
 * or survived dark mode. Every colour now lives in `WidgetCardChrome.module.css`
 * and none of it is inline — which is the mechanically checkable form of the
 * rule (`WidgetCard.test.tsx` asserts the rendered chrome carries no colour in a
 * `style` attribute, so a future `color="blue"` cannot creep back in unnoticed).
 *
 * ⚠️ THIS FILE IS THE READ-ONLY BOUNDARY, and it is a separate module for that
 * reason. `WidgetCard` renders whatever arrives through its `chrome` SLOT and
 * owns no edit affordance of its own; the affordances live HERE, and the viewer
 * never imports this module. So "reader" remains the state you get by omission
 * rather than the result of remembering a `readOnly` flag — see the note at the
 * top of `WidgetCard.tsx`.
 */

/**
 * Marks the element the resize grips reveal on hover — the `<Rnd>` FRAME, not
 * the card: the grips are rendered by re-resizable as siblings of the card, so a
 * `[data-widget-card]:hover` rule would never match them.
 *
 * Spread onto the `<Rnd>` rather than written as a literal at the call site so
 * the attribute and the CSS that reads it cannot drift apart.
 */
export const WIDGET_FRAME_PROPS = { 'data-widget-frame': '' } as const;

/**
 * Geometry for re-resizable's hit areas — NOT appearance (there is no colour
 * here; the drawing is `.handle`'s, in the CSS module).
 *
 * It exists because the library's defaults hang every handle HALF OUTSIDE the
 * element: `right: -5px` on the edges and `right/bottom: -10px` on the corner
 * (`re-resizable/lib/resizer.js`). That is invisible while the grips are
 * transparent — which is exactly what the old chrome was — but the moment they
 * are drawn, a card's right-edge grip sits on top of its NEIGHBOUR. Pulling them
 * back inside the card is the whole change; the sizes are the prototype's.
 */
export const WIDGET_RESIZE_HANDLE_STYLES: Record<
  'right' | 'bottom' | 'bottomRight',
  CSSProperties
> = {
  right: { top: 0, right: 0, width: '12px', height: '100%', cursor: 'ew-resize' },
  bottom: { left: 0, bottom: 0, height: '12px', width: '100%', cursor: 'ns-resize' },
  bottomRight: { right: 0, bottom: 0, width: '18px', height: '18px', cursor: 'nwse-resize' }
};

export type WidgetCardActionsProps = {
  /** The widget the bar acts on — keys every testid, so a test can address one card. */
  widgetId: string;
  onEdit: () => void;
  onRemove: () => void;
};

/**
 * The hover action bar: edit, remove. Handed to `<WidgetCard chrome={...}>`.
 *
 * `variant="default"` rather than `variant="filled" color="blue"`: `default`
 * resolves through Mantine's SEMANTIC surface variables, which are already
 * scheme-aware, so the button has a real surface and border in both themes
 * before this module's own `light-dark()` rules refine it. A `color` prop would
 * instead emit an inline `--ai-bg: var(--mantine-color-blue-filled)` — a fixed
 * palette entry, inline, which is precisely the offence this ticket removes.
 */
export function WidgetCardActions({ widgetId, onEdit, onRemove }: WidgetCardActionsProps ) {
  const { t } = useTranslation( 'widgets' );

  return (
    <div className={classes.actions}>
      <ActionIcon
        size="sm"
        radius="xs"
        variant="default"
        className={classes.actionButton}
        data-testid={componentsTestIds.reportsCardEdit( widgetId )}
        onClick={onEdit}
        aria-label={t( 'edit_widget', 'Edit widget' )}
      >
        <IconEdit size={13} stroke={1.6} />
      </ActionIcon>
      <ActionIcon
        size="sm"
        radius="xs"
        variant="default"
        className={`${ classes.actionButton } ${ classes.removeButton }`}
        data-testid={componentsTestIds.reportsCardRemove( widgetId )}
        onClick={onRemove}
        aria-label={t( 'remove_widget', 'Remove widget' )}
      >
        <IconX size={13} stroke={1.6} />
      </ActionIcon>
    </div>
  );
}

/**
 * The three resize grips, in the shape `<Rnd resizeHandleComponent>` wants.
 *
 * ⚠️ THE TESTIDS ARE LOAD-BEARING (TV1-2291 · t10). They are the ONLY oracle the
 * resize branch has: before ticket 10 the grips were anonymous inline-styled
 * divs and `WidgetLayoutEditor.test.tsx` had no way to press one, so the resize
 * half of the placement wiring was untested. Restyling the grips must not cost
 * that — hence they survive this rebuild unchanged, keyed by widget id AND
 * react-rnd direction.
 */
export const widgetResizeHandles = (
  widgetId: string
): Record<'right' | 'bottom' | 'bottomRight', ReactElement> => ({
  right: (
    <div
      data-testid={componentsTestIds.widgetResizeHandle( widgetId, 'right' )}
      className={`${ classes.handle } ${ classes.handleEdgeRight }`}
    />
  ),
  bottom: (
    <div
      data-testid={componentsTestIds.widgetResizeHandle( widgetId, 'bottom' )}
      className={`${ classes.handle } ${ classes.handleEdgeBottom }`}
    />
  ),
  bottomRight: (
    <div
      data-testid={componentsTestIds.widgetResizeHandle( widgetId, 'bottomRight' )}
      className={`${ classes.handle } ${ classes.handleCorner }`}
    />
  )
});
